// patientSearch.tsx - UPDATED VERSION
import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Button,
  Paper,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  SxProps,
  Theme,
  useTheme,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Search,
  HealthAndSafety,
  Explore,
  MyLocation,
} from '@mui/icons-material'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import api from '@/services/api'
import { useGooglePlaces, PlaceDetails } from '@/hooks/useGooglePlaces'
import {
  usePatientSearchStore,
  LocationData as StoreLocationData,
} from '@/store/usePatientSearchStore' // ✅ Changed import

// Types for API responses
interface InsuranceCompany {
  _id: string
  payer_name: string
  payerid: string
}

interface Specialty {
  _id: string
  name: string
  description?: string
}

interface LocationData {
  address: string
  latitude: number
  longitude: number
}

interface SearchParams {
  insurance?: string
  specialty?: string
  radius?: string
  location?: string
  lat?: number
  lng?: number
  insuranceId?: string
  specialtyId?: string
}

interface SearchBarForPatientProps {
  locations: string | null
  radius: string | null
  insurance: string | null
  needs: string | null
  onLocationChange: (value: string | null) => void
  onRadiusChange: (value: string | null) => void
  onInsuranceChange: (value: string | null) => void
  onNeedsChange: (value: string | null) => void
  onSearch?: (searchParams: SearchParams) => void
  showSearchButton?: boolean
  compact?: boolean
  sx?: SxProps<Theme>
}

// Default options for fallback
const DEFAULT_SEARCH_RADIUS = [
  '5 miles',
  '10 miles',
  '15 miles',
  '20 miles',
  '25 miles',
]

const SearchBarForPatient: React.FC<SearchBarForPatientProps> = ({
  locations,
  radius,
  insurance,
  needs,
  onLocationChange,
  onRadiusChange,
  onInsuranceChange,
  onNeedsChange,
  onSearch,
  showSearchButton = true,
  compact = false,
  sx,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // State for hover expansion
  const [hoveredField, setHoveredField] = useState<
    'location' | 'radius' | 'insurance' | 'needs' | null
  >(null)

  // State for dynamic data
  const [insuranceCompanies, setInsuranceCompanies] = useState<
    InsuranceCompany[]
  >([])
  const [specialties, setSpecialties] = useState<Specialty[]>([])
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const [loadingSpecialties, setLoadingSpecialties] = useState(false)
  const [insuranceSearchTerm, setInsuranceSearchTerm] = useState('')
  const [specialtySearchTerm, setSpecialtySearchTerm] = useState('')

  // Location states
  const [locationInput, setLocationInput] = useState('')
  const [locationOptions, setLocationOptions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])
  const [loadingLocations, setLoadingLocations] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    null
  )
  const [gettingCurrentLocation, setGettingCurrentLocation] = useState(false)

  // ✅ Changed to use patient search store
  const {
    setRadius: setStoreRadius,
    setSpecialty,
    setInsurance,
    setLocation: setStoreLocation,
    location: storeLocation,
    radius: storeRadius,
    insuranceId,
    specialtyId,
    insuranceName,
    specialtyName,
    clearSearch,
  } = usePatientSearchStore()

  // Add ref to track the last selected specialty
  const lastSelectedSpecialtyRef = useRef<{
    id: string | null
    name: string | null
  }>({ id: null, name: null })

  // ✅ Sync component state with store state
  useEffect(() => {
    if (storeLocation?.address && storeLocation.address !== locationInput) {
      setLocationInput(storeLocation.address)
      setSelectedLocation({
        address: storeLocation.address,
        latitude: storeLocation.lat || 0,
        longitude: storeLocation.lng || 0,
      })
    }
  }, [storeLocation])

  useEffect(() => {
    if (storeRadius && radius !== storeRadius) {
      onRadiusChange?.(storeRadius + ' miles')
    }
  }, [storeRadius])

  useEffect(() => {
    if (insuranceName && insurance !== insuranceName) {
      onInsuranceChange(insuranceName)
    }
  }, [insuranceName])

  // ✅ Sync specialty with better handling - UPDATED
  useEffect(() => {
    if (specialtyName !== null && needs !== specialtyName) {
      onNeedsChange(specialtyName)
    }
    // Also handle case where specialtyName is null but needs has value
    if (!specialtyName && needs) {
      // If store has no specialty but component has needs, update store with custom input
      setSpecialty(null, needs)
    }
  }, [specialtyName])

  // ✅ Log when store values change - ADD THIS
  useEffect(() => {
    console.log('Patient Search Store state updated:', {
      insuranceId,
      insuranceName,
      specialtyId,
      specialtyName,
      location: storeLocation,
      radius: storeRadius,
    })
  }, [
    insuranceId,
    insuranceName,
    specialtyId,
    specialtyName,
    storeLocation,
    storeRadius,
  ])

  const {
    isLoaded,
    isLoading: isGoogleLoading,
    searchPlaces,
    getPlaceDetails,
    getCurrentLocation,
  } = useGooglePlaces()

  // Fetch insurance companies
  const fetchInsuranceCompanies = async (search: string = '') => {
    setLoadingCompanies(true)
    try {
      const response = await api.get(`/payers/all?search=${search}`, {
        timeout: 15000,
      })
      const companies = response.data?.data || response.data || []
      setInsuranceCompanies(companies)
    } catch (error: any) {
      console.error('Error fetching insurance companies:', error)
      setInsuranceCompanies([])
    } finally {
      setLoadingCompanies(false)
    }
  }

  // Fetch specialties with search - UPDATED WITH DEBUGGING
  const fetchSpecialties = async (search: string = '') => {
    setLoadingSpecialties(true)
    try {
      const response = await api.get(
        `specilities/specility${search ? `?search=${search}` : ''}`,
        {
          timeout: 15000,
        }
      )
      const specialtiesData = response.data?.data || response.data || []
      console.log('Fetched specialties in patientSearch:', specialtiesData)
      console.log('First specialty structure:', specialtiesData[0])
      setSpecialties(specialtiesData)
    } catch (error: any) {
      console.error('Error fetching specialties:', error)
      setSpecialties([])
    } finally {
      setLoadingSpecialties(false)
    }
  }

  // Fetch location predictions from Google Places
  const fetchLocationPredictions = async (input: string) => {
    if (!input.trim()) {
      setLocationOptions([])
      return
    }

    setLoadingLocations(true)
    try {
      const predictions = await searchPlaces(input)
      setLocationOptions(predictions)
    } catch (error) {
      console.error('Error fetching location predictions:', error)
      setLocationOptions([])
    } finally {
      setLoadingLocations(false)
    }
  }

  // Handle specialty change - ADD THIS FUNCTION
  const handleSpecialtyChange = (value: string | Specialty | null) => {
    console.log('handleSpecialtyChange called in patientSearch with:', value)

    if (!value) {
      onNeedsChange(null)
      setSpecialty(null, null)
      lastSelectedSpecialtyRef.current = { id: null, name: null }
      return
    }

    if (typeof value === 'string') {
      // Custom input
      onNeedsChange(value)
      setSpecialty(null, value)
      lastSelectedSpecialtyRef.current = { id: null, name: value }
    } else {
      // Selected from dropdown
      onNeedsChange(value.name)
      setSpecialty(value._id, value.name)
      lastSelectedSpecialtyRef.current = { id: value._id, name: value.name }
    }

    // Verify store update after a short delay
    setTimeout(() => {
      const storeState = usePatientSearchStore.getState()
      console.log('Patient Search Store after specialty change:', {
        specialtyId: storeState.specialtyId,
        specialtyName: storeState.specialtyName,
      })
    }, 100)
  }

  // Handle current location button click
  const handleCurrentLocation = async () => {
    setGettingCurrentLocation(true)
    try {
      const currentLocation = await getCurrentLocation()
      setSelectedLocation(currentLocation)
      onLocationChange(currentLocation.address)
      setLocationInput(currentLocation.address)
      // ✅ Update patient store
      setStoreLocation({
        address: currentLocation.address,
        lat: currentLocation.latitude,
        lng: currentLocation.longitude,
      })
    } catch (error) {
      console.error('Error getting current location:', error)
      // You might want to show a snackbar or toast notification here
    } finally {
      setGettingCurrentLocation(false)
    }
  }

  // Handle location selection with coordinates
  const handleLocationSelect = async (placeId: string, description: string) => {
    try {
      const placeDetails = await getPlaceDetails(placeId)

      setSelectedLocation(placeDetails)

      // ✅ Update patient store
      setStoreLocation({
        address: description,
        lat: placeDetails.latitude,
        lng: placeDetails.longitude,
      })

      setLocationInput(description)
    } catch (error) {
      console.error('Error getting place details:', error)

      const tempLocation = {
        address: description,
        latitude: 0,
        longitude: 0,
      }

      setSelectedLocation(tempLocation)

      // ✅ Update patient store
      setStoreLocation({
        address: description,
        lat: 0,
        lng: 0,
      })

      setLocationInput(description)
    }
  }

  // Handle search with coordinates - UPDATED WITH DIRECT STORE ACCESS
  const handleSearch = () => {
    // Get the current store state directly
    const currentStore = usePatientSearchStore.getState()

    console.log('=== PATIENT SEARCH STORE STATE ===')
    console.log('Full store state:', currentStore)
    console.log('Specialty in store:', {
      specialtyId: currentStore.specialtyId,
      specialtyName: currentStore.specialtyName,
    })
    console.log(
      'Last selected specialty ref:',
      lastSelectedSpecialtyRef.current
    )
    console.log('Component state:', {
      insurance,
      needs,
      radius,
      locationInput,
    })
    console.log('=== END STORE STATE ===')

    // Use store state directly, fallback to ref if needed
    const effectiveSpecialtyId =
      currentStore.specialtyId || lastSelectedSpecialtyRef.current.id
    const effectiveSpecialtyName =
      currentStore.specialtyName ||
      lastSelectedSpecialtyRef.current.name ||
      needs

    onSearch?.({
      insurance: currentStore.insuranceName || insurance || null,
      specialty: effectiveSpecialtyName || null,
      insuranceId: currentStore.insuranceId || null,
      specialtyId: effectiveSpecialtyId || null,
      radius: currentStore.radius || radius?.replace(' miles', '') || null,
      location:
        currentStore.location?.address ||
        selectedLocation?.address ||
        locationInput ||
        null,
      lat: currentStore.location?.lat || selectedLocation?.latitude || null,
      lng: currentStore.location?.lng || selectedLocation?.longitude || null,
    })
  }

  // Debounced effect for location predictions
  useEffect(() => {
    if (!isLoaded) return

    const delayDebounceFn = setTimeout(() => {
      fetchLocationPredictions(locationInput)
    }, 300)

    return () => clearTimeout(delayDebounceFn)
  }, [locationInput, isLoaded])

  // Effects for initial data loading
  useEffect(() => {
    fetchSpecialties()
  }, [])

  // Effect for debounced insurance search - only fetch when there's a search term
  useEffect(() => {
    // Only fetch if there's a search term
    if (!insuranceSearchTerm.trim()) {
      return
    }

    const delayDebounceFn = setTimeout(() => {
      fetchInsuranceCompanies(insuranceSearchTerm)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [insuranceSearchTerm])

  // Effect for debounced specialty search - ADD THIS
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchSpecialties(specialtySearchTerm)
    }, 500)

    return () => clearTimeout(delayDebounceFn)
  }, [specialtySearchTerm])

  return (
    <Paper
      elevation={3}
      sx={{
        border: '1px solid lightgray',
        borderRadius: compact ? 3 : { xs: 2, sm: 3, md: 4 },
        overflow: 'hidden',
        boxShadow: compact
          ? '1px 2px 8px rgba(0,0,0,0.1)'
          : '1px 8px 32px rgba(0,0,0,0.1)',
        transition: 'all 0.3s ease',
        height: compact ? { xs: '50px', sm: '60px' } : 'auto',
        '&:hover': {
          boxShadow: compact
            ? '1px 4px 12px rgba(0,0,0,0.15)'
            : '1px 12px 40px rgba(0,0,0,0.15)',
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: {
            xs: compact ? 'nowrap' : 'wrap',
            sm: 'nowrap',
            md: 'nowrap',
          },
          height: compact ? '100%' : 'auto',
          width: '100%',
          position: 'relative',
        }}
      >
        {/* Dynamic Location Input */}
        <FilterBox
          compact={compact}
          isFirst={true}
          hoveredField={hoveredField}
          fieldName="location"
          onMouseEnter={() => setHoveredField('location')}
          onMouseLeave={() => setHoveredField(null)}
        >
          <FilterLabel title="Search Location" compact={compact} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Autocomplete
              freeSolo
              options={locationOptions}
              getOptionLabel={option => {
                if (typeof option === 'string') return option
                return option.description
              }}
              value={locationInput}
              inputValue={locationInput}
              // 🌟 FIXED: detect real Google place selection
              onChange={(event, value) => {
                if (!value) {
                  setSelectedLocation(null)
                  // ✅ Update patient store
                  setStoreLocation({ address: '', lat: null, lng: null })
                  // Clear radius when location is cleared
                  setStoreRadius('')
                  onRadiusChange(null)
                  onLocationChange(null)
                  setLocationInput('')
                  return
                }

                if (typeof value === 'string') {
                  // manual input
                  setSelectedLocation({
                    address: value,
                    latitude: 0,
                    longitude: 0,
                  })

                  // ✅ Update patient store
                  setStoreLocation({
                    address: value,
                    lat: 0,
                    lng: 0,
                  })

                  onLocationChange(value)
                  setLocationInput(value)
                  return
                }

                // 🌟 User clicked a GOOGLE prediction
                if (value.place_id) {
                  handleLocationSelect(value.place_id, value.description)
                }
              }}
              onInputChange={(event, value) => {
                setLocationInput(value)
                onLocationChange(value)
              }}
              loading={isGoogleLoading || loadingLocations}
              renderInput={params => (
                <StyledInput
                  {...params}
                  icon={
                    <MyLocationIcon
                      sx={{
                        fontSize: { xs: 16, sm: 18, md: 20 },
                        color: '#3BAE78',
                      }}
                    />
                  }
                  placeholder="City, State or Zip code"
                  compact={compact}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {(isGoogleLoading || loadingLocations) && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              filterOptions={x => x}
              noOptionsText={
                locationInput
                  ? 'No locations found'
                  : 'Start typing to search locations'
              }
              sx={{ flex: 1 }}
            />

            <Tooltip title="Use current location">
              <IconButton
                onClick={handleCurrentLocation}
                disabled={gettingCurrentLocation}
                sx={{
                  color: gettingCurrentLocation ? 'grey.400' : '#3BAE78',
                  p: compact ? 0 : 0,
                }}
              >
                {gettingCurrentLocation ? <CircularProgress size={10} /> : null}
              </IconButton>
            </Tooltip>
          </Box>
        </FilterBox>

        {/* Radius Input */}
        <FilterBox
          compact={compact}
          hoveredField={hoveredField}
          fieldName="radius"
          onMouseEnter={() => setHoveredField('radius')}
          onMouseLeave={() => setHoveredField(null)}
        >
          <FilterLabel title="Search Radius" compact={compact} />

          <Autocomplete
            freeSolo
            options={DEFAULT_SEARCH_RADIUS}
            sx={{ width: '100%' }}
            disabled={!locations || !locations.trim()}
            renderInput={params => (
              <StyledInput
                {...params}
                icon={
                  <Explore
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 20 },
                      color:
                        !locations || !locations.trim()
                          ? 'grey.400'
                          : '#3BAE78',
                    }}
                  />
                }
                placeholder={
                  !locations || !locations.trim()
                    ? 'Select location first'
                    : isSmallMobile && compact
                      ? 'Radius'
                      : '5 miles, 10 miles or custom'
                }
                compact={compact}
                disabled={!locations || !locations.trim()}
              />
            )}
            value={radius}
            onChange={(e, v) => {
              if (!locations || !locations.trim()) return
              onRadiusChange(v)
              // ✅ Update patient store
              setStoreRadius(v?.replace(' miles', '') || '')
            }}
            onInputChange={(e, v) => {
              if (!locations || !locations.trim()) return
              onRadiusChange(v)
              // ✅ Update patient store
              setStoreRadius(v?.replace(' miles', '') || '')
            }}
          />
        </FilterBox>

        {/* Insurance Input */}
        <FilterBox
          compact={compact}
          hoveredField={hoveredField}
          fieldName="insurance"
          onMouseEnter={() => setHoveredField('insurance')}
          onMouseLeave={() => setHoveredField(null)}
        >
          <FilterLabel title="Insurance" compact={compact} />
          <Autocomplete
            freeSolo
            options={insuranceCompanies}
            getOptionLabel={option => {
              if (typeof option === 'string') return option
              return option.payer_name
            }}
            // Controlled value
            value={insurance}
            // When user selects from dropdown
            onChange={(e, v) => {
              const name = typeof v === 'string' ? v : v?.payer_name || null
              const id = typeof v === 'string' ? null : v?._id || null

              onInsuranceChange(name) // Old local handler (optional)
              // ✅ Update patient store
              setInsurance(id, name)
            }}
            // When user types text manually
            onInputChange={(e, v, reason) => {
              setInsuranceSearchTerm(v)
              onInsuranceChange(v)
              // ✅ Update patient store for input changes - FIXED
              if (reason === 'input') {
                setInsurance(null, v || null)
              } else if (reason === 'clear') {
                setInsurance(null, null)
              }
            }}
            loading={loadingCompanies}
            renderInput={params => (
              <StyledInput
                {...params}
                icon={
                  <HealthAndSafety
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 20 },
                      color: '#3BAE78',
                    }}
                  />
                }
                placeholder={
                  isSmallMobile && compact ? 'Insurance' : 'Select Insurance'
                }
                compact={compact}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCompanies ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            filterOptions={(options, state) => {
              if (!state.inputValue) return options
              return options.filter(option =>
                option.payer_name
                  .toLowerCase()
                  .includes(state.inputValue.toLowerCase())
              )
            }}
            renderOption={(props, option) => (
              <li
                {...props}
                key={typeof option === 'string' ? option : option._id}
              >
                {typeof option === 'string' ? option : option.payer_name}
              </li>
            )}
          />
        </FilterBox>

        {/* Needs Input (Specialties) - UPDATED */}
        <FilterBox
          compact={compact}
          isLast={!showSearchButton}
          hoveredField={hoveredField}
          fieldName="needs"
          onMouseEnter={() => setHoveredField('needs')}
          onMouseLeave={() => setHoveredField(null)}
        >
          <FilterLabel title="Needs" compact={compact} />
          <Autocomplete
            freeSolo
            options={specialties}
            getOptionLabel={option => {
              console.log(
                'Patient Search Specialty getOptionLabel called with:',
                option
              )
              if (typeof option === 'string') return option
              return option.name
            }}
            value={needs}
            onChange={(e, v) => {
              console.log(
                'Patient Search Specialty onChange triggered with:',
                v
              )
              handleSpecialtyChange(v)
            }}
            // When user types text manually
            onInputChange={(e, v, reason) => {
              console.log('Patient Search Specialty onInputChange triggered:', {
                value: v,
                reason,
              })
              setSpecialtySearchTerm(v)

              if (reason === 'clear') {
                handleSpecialtyChange(null)
              } else if (reason === 'input') {
                handleSpecialtyChange(v)
              }
            }}
            loading={loadingSpecialties}
            renderInput={params => (
              <StyledInput
                {...params}
                icon={
                  <AddCircleIcon
                    sx={{
                      fontSize: { xs: 16, sm: 18, md: 20 },
                      color: '#3BAE78',
                    }}
                  />
                }
                placeholder={
                  isSmallMobile && compact ? 'Needs' : 'What do you need?'
                }
                compact={compact}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingSpecialties ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            filterOptions={(options, state) => {
              if (!state.inputValue) return options
              return options.filter(option =>
                option.name
                  .toLowerCase()
                  .includes(state.inputValue.toLowerCase())
              )
            }}
            renderOption={(props, option) => {
              console.log(
                'Patient Search Specialty renderOption called for:',
                option
              )
              return (
                <li
                  {...props}
                  key={typeof option === 'string' ? option : option._id}
                >
                  {typeof option === 'string' ? option : option.name}
                </li>
              )
            }}
          />
        </FilterBox>

        {/* Search Button */}
        {showSearchButton && (
          <Button
            variant="contained"
            onClick={handleSearch}
            sx={{
              m: compact ? 0.5 : { xs: 1, sm: 0.5 },
              // ✅ Use store location or selected location for button state
              bgcolor: '#3BAE78',
              borderRadius: compact ? 2 : { xs: 2, sm: 2.2 },
              minWidth: { xs: '100%', sm: compact ? '50px' : '60px' },
              width: { xs: 'calc(100% - 16px)', sm: 'auto' },
              height: compact
                ? { xs: '40px', sm: 'auto' }
                : { xs: '48px', sm: 'auto' },
              flex: { xs: '0 0 auto', sm: '0 0 auto' },
              alignSelf: { xs: 'center', sm: 'stretch' },
              '&:hover': {
                bgcolor: '#2d8c5f',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(59, 174, 120, 0.3)',
              },
              transition: 'all 0.3s ease',
              fontSize: { xs: '0.875rem', sm: '1rem' },
            }}
          >
            <Search
              sx={{
                color: 'white',
                fontSize: { xs: 20, sm: 24 },
              }}
            />
          </Button>
        )}
      </Box>
    </Paper>
  )
}

// Keep your existing FilterBox, FilterLabel, and StyledInput components
const FilterBox = ({
  children,
  compact,
  isFirst = false,
  isLast = false,
  hoveredField,
  fieldName,
  onMouseEnter,
  onMouseLeave,
}: any) => {
  const isHovered = hoveredField === fieldName
  const isAnyHovered = hoveredField !== null

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        marginBottom: 0,
        flexGrow: {
          xs: 1,
          sm: 1,
          md: isHovered ? 2 : isAnyHovered ? 0.8 : 1,
        },
        flexShrink: {
          xs: 1,
          sm: 1,
          md: isHovered ? 0 : 1,
        },
        flexBasis: {
          xs: '100%',
          sm: compact ? '0%' : '33%',
          md: isHovered ? 'auto' : '0%',
        },
        minWidth: {
          md: isHovered ? '100px' : '0',
        },
        borderRight: {
          xs: 'none',
          sm: isLast && !compact ? 'none' : '1px solid #e0e0e0',
        },
        borderBottom: {
          xs: compact ? 'none' : '1px solid #e0e0e0',
          sm: 'none',
        },
        borderColor: 'grey.200',
        bgcolor: 'grey.50',
        transition:
          'flex-grow 0.4s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.4s cubic-bezier(0.4, 0, 0.2, 1), flex-shrink 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        p: compact ? { xs: 0.75, sm: 1 } : { xs: 1.5, sm: 2 },
        minHeight: compact ? 'auto' : { xs: '60px', sm: '80px' },
        zIndex: isHovered ? 1 : 0,
        position: 'relative',
        '&:hover': {
          bgcolor: 'grey.100',
        },
        '&:first-of-type': {
          borderTopLeftRadius: { sm: compact ? 0 : 4 },
          borderBottomLeftRadius: { sm: compact ? 0 : 4 },
        },
        '&:last-of-type': {
          borderBottomRightRadius: { sm: compact ? 0 : 4 },
        },
      }}
    >
      {children}
    </Box>
  )
}

const FilterLabel = ({
  title,
  compact,
}: {
  title: string
  compact: boolean
}) => (
  <Typography
    variant="caption"
    sx={{
      color: 'grey.600',
      mb: compact ? { xs: 0, sm: 0.25 } : { xs: 0.5, sm: 1 },
      fontSize: { xs: '0.7rem', sm: '0.75rem' },
      fontWeight: compact ? 500 : 400,
      display: { xs: compact ? 'none' : 'block', sm: 'block' },
      lineHeight: 1.2,
    }}
  >
    {title}
  </Typography>
)

const StyledInput = ({
  icon,
  placeholder,
  compact,
  InputProps,
  ...params
}: any) => (
  <TextField
    {...params}
    placeholder={placeholder}
    variant="standard"
    InputProps={{
      ...params.InputProps,
      ...InputProps,
      startAdornment: (
        <InputAdornment
          position="start"
          sx={{
            mr: { xs: 0.5, sm: 1 },
            minWidth: 'auto',
          }}
        >
          {icon}
        </InputAdornment>
      ),
      disableUnderline: true,
      sx: {
        fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
        height: compact
          ? { xs: '28px', sm: '32px' }
          : { xs: '36px', sm: '40px' },
        '& .MuiInputBase-input': {
          padding: compact ? '4px 0' : '8px 0',
          fontSize: 'inherit',
          '&::placeholder': {
            fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
            opacity: 0.7,
          },
        },
      },
    }}
    sx={{
      width: '100%',
      minWidth: '0 !important',
      '& .MuiInputBase-root': {
        padding: 0,
        height: '100%',
      },
      ...params.sx,
    }}
  />
)

export default SearchBarForPatient

// import React, { useState, useEffect } from 'react'
// import {
//   Box,
//   Button,
//   Paper,
//   Typography,
//   Autocomplete,
//   TextField,
//   InputAdornment,
//   SxProps,
//   Theme,
//   useTheme,
//   useMediaQuery,
//   CircularProgress,
// } from '@mui/material'
// import {
//   Search,
//   HealthAndSafety,
//   Explore,
// } from '@mui/icons-material'
// import MyLocationIcon from '@mui/icons-material/MyLocation'
// import AddCircleIcon from '@mui/icons-material/AddCircle';
// import api from '@/services/api';
// import { useGooglePlaces } from '@/hooks/useGooglePlaces';

// // Types for API responses
// interface InsuranceCompany {
//   _id: string;
//   payer_name: string;
//   payerid: string;
// }

// interface Specialty {
//   _id: string;
//   name: string;
//   description?: string;
// }

// interface SearchParams {
//   insurance?: string;
//   specialty?: string;
//   radius?: string;
//   location?: string;
// }

// interface SearchBarForPatientProps {
//   locations: string | null
//   radius: string | null
//   insurance: string | null
//   needs: string | null
//   onLocationChange: (value: string | null) => void
//   onRadiusChange: (value: string | null) => void
//   onInsuranceChange: (value: string | null) => void
//   onNeedsChange: (value: string | null) => void
//   onSearch?: (searchParams: SearchParams) => void
//   showSearchButton?: boolean
//   compact?: boolean
//   sx?: SxProps<Theme>
// }

// // Default options for fallback
// const DEFAULT_SEARCH_RADIUS = ['5 km', '10 km', '20 km', '40 km']

// const SearchBarForPatient: React.FC<SearchBarForPatientProps> = ({
//   locations,
//   radius,
//   insurance,
//   needs,
//   onLocationChange,
//   onRadiusChange,
//   onInsuranceChange,
//   onNeedsChange,
//   onSearch,
//   showSearchButton = true,
//   compact = false,
//   sx,
// }) => {
//   const theme = useTheme()
//   const isMobile = useMediaQuery(theme.breakpoints.down('md'))
//   const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'))

//   // State for dynamic data
//   const [insuranceCompanies, setInsuranceCompanies] = useState<InsuranceCompany[]>([])
//   const [specialties, setSpecialties] = useState<Specialty[]>([])
//   const [loadingCompanies, setLoadingCompanies] = useState(false)
//   const [loadingSpecialties, setLoadingSpecialties] = useState(false)
//   const [insuranceSearchTerm, setInsuranceSearchTerm] = useState('')

//   // Location states
//   const [locationInput, setLocationInput] = useState('')
//   const [locationOptions, setLocationOptions] = useState<string[]>([])
//   const [loadingLocations, setLoadingLocations] = useState(false)

//   // Use your Google Places hook
//   const { isLoaded, isLoading: isGoogleLoading, autocompleteService } = useGooglePlaces()

//   // Fetch insurance companies with better error handling
//   const fetchInsuranceCompanies = async (search: string = '') => {
//     setLoadingCompanies(true)
//     try {
//       const response = await api.get(`/payers/all?search=${search}`, {
//         timeout: 15000 // Increase timeout to 15 seconds
//       })
//       const companies = response.data?.data || response.data || []
//       setInsuranceCompanies(companies)
//     } catch (error: any) {
//       console.error('Error fetching insurance companies:', error)
//       if (error.code === 'ECONNABORTED') {
//         console.warn('Insurance companies request timed out')
//         // You can set a fallback or show a message to the user
//       }
//       setInsuranceCompanies([])
//     } finally {
//       setLoadingCompanies(false)
//     }
//   }

//   // Fetch specialties with better error handling
//   const fetchSpecialties = async () => {
//     setLoadingSpecialties(true)
//     try {
//       const response = await api.get('specilities/specility', {
//         timeout: 15000
//       })
//       const specialtiesData = response.data?.data || response.data || []
//       setSpecialties(specialtiesData)
//     } catch (error: any) {
//       console.error('Error fetching specialties:', error)
//       if (error.code === 'ECONNABORTED') {
//         console.warn('Specialties request timed out')
//       }
//       setSpecialties([])
//     } finally {
//       setLoadingSpecialties(false)
//     }
//   }

//   // Fetch location predictions from Google Places - FIXED TYPE ISSUE
//   const fetchLocationPredictions = async (input: string) => {
//     if (!autocompleteService || !input.trim()) {
//       setLocationOptions([])
//       return
//     }

//     setLoadingLocations(true)
//     try {
//       // Use only one type to avoid the mixing error
//       const request = {
//         input: input,
//         types: ['(regions)'] as any, // Use '(regions)' which includes cities, states, etc.
//       };

//       autocompleteService.getPlacePredictions(
//         request,
//         (predictions: google.maps.places.AutocompletePrediction[] | null, status: google.maps.places.PlacesServiceStatus) => {
//           setLoadingLocations(false)
//           if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
//             const descriptions = predictions.map(prediction => prediction.description)
//             setLocationOptions(descriptions)
//           } else {
//             setLocationOptions([])
//           }
//         }
//       )
//     } catch (error) {
//       console.error('Error fetching location predictions:', error)
//       setLoadingLocations(false)
//       setLocationOptions([])
//     }
//   }

//   // Handle search
//   const handleSearch = () => {
//     if (onSearch) {
//       const searchParams: SearchParams = {
//         insurance: insurance || '',
//         specialty: needs || '',
//         radius: radius ? radius.replace(' km', '') : '',
//         location: locations || ''
//       }
//       onSearch(searchParams)
//     }
//   }

//   // Handle location input change with debouncing
//   const handleLocationInputChange = (event: React.SyntheticEvent, value: string) => {
//     setLocationInput(value)
//   }

//   // Handle location selection
//   const handleLocationChange = (event: React.SyntheticEvent, value: string | null) => {
//     onLocationChange(value)
//     if (value) {
//       setLocationInput(value)
//     }
//   }

//   // Debounced effect for location predictions
//   useEffect(() => {
//     if (!isLoaded) return

//     const delayDebounceFn = setTimeout(() => {
//       fetchLocationPredictions(locationInput)
//     }, 300)

//     return () => clearTimeout(delayDebounceFn)
//   }, [locationInput, isLoaded, autocompleteService])

//   // Effects for initial data loading
//   useEffect(() => {
//     fetchInsuranceCompanies()
//     fetchSpecialties()
//   }, [])

//   // Effect for debounced insurance search
//   useEffect(() => {
//     const delayDebounceFn = setTimeout(() => {
//       fetchInsuranceCompanies(insuranceSearchTerm)
//     }, 500)

//     return () => clearTimeout(delayDebounceFn)
//   }, [insuranceSearchTerm])

//   return (
//     <Paper
//       elevation={3}
//       sx={{
//         border: '1px solid lightgray',
//         borderRadius: compact ? 3 : { xs: 2, sm: 3, md: 4 },
//         overflow: 'hidden',
//         boxShadow: compact
//           ? '1px 2px 8px rgba(0,0,0,0.1)'
//           : '1px 8px 32px rgba(0,0,0,0.1)',
//         transition: 'all 0.3s ease',
//         height: compact ? { xs: '50px', sm: '60px' } : 'auto',
//         '&:hover': {
//           boxShadow: compact
//             ? '1px 4px 12px rgba(0,0,0,0.15)'
//             : '1px 12px 40px rgba(0,0,0,0.15)',
//         },
//         ...sx,
//       }}
//     >
//       <Box
//         sx={{
//           display: 'flex',
//           alignItems: 'stretch',
//           flexDirection: { xs: 'column', sm: 'row' },
//           flexWrap: compact ? 'nowrap' : { xs: 'wrap', sm: 'nowrap' },
//           height: compact ? '100%' : 'auto',
//           width: '100%',
//         }}
//       >
//         {/* Dynamic Location Input */}
//         <FilterBox compact={compact} isFirst={true}>
//           <FilterLabel title="Add Location" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={locationOptions}
//             value={locations}
//             inputValue={locationInput}
//             onInputChange={handleLocationInputChange}
//             onChange={handleLocationChange}
//             loading={isGoogleLoading || loadingLocations}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<MyLocationIcon sx={{
//                   fontSize: { xs: 16, sm: 18, md: 20 },
//                   color: '#3BAE78'
//                 }} />}
//                 placeholder={isSmallMobile && compact ? "Location" : "City, State or Zip code"}
//                 compact={compact}
//                 InputProps={{
//                   ...params.InputProps,
//                   endAdornment: (
//                     <>
//                       {(isGoogleLoading || loadingLocations) ? <CircularProgress color="inherit" size={20} /> : null}
//                       {params.InputProps.endAdornment}
//                     </>
//                   ),
//                 }}
//               />
//             )}
//             filterOptions={(x) => x}
//             noOptionsText={locationInput ? "No locations found" : "Start typing to search locations"}
//           />
//         </FilterBox>

//         {/* Radius Input */}
//         <FilterBox compact={compact}>
//           <FilterLabel title="Search Radius" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={DEFAULT_SEARCH_RADIUS}
//             value={radius}
//             onChange={(e, v) => onRadiusChange(v)}
//             onInputChange={(e, v) => onRadiusChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<Explore sx={{
//                   fontSize: { xs: 16, sm: 18, md: 20 },
//                   color: '#3BAE78'
//                 }} />}
//                 placeholder={isSmallMobile && compact ? "Radius" : "5 km, 10 km or custom"}
//                 compact={compact}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Insurance Input */}
//         <FilterBox compact={compact}>
//           <FilterLabel title="Insurance" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={insuranceCompanies}
//             getOptionLabel={(option) => {
//               if (typeof option === 'string') return option
//               return option.payer_name
//             }}
//             value={insurance}
//             onChange={(e, v) => onInsuranceChange(typeof v === 'string' ? v : v?.payer_name || null)}
//             onInputChange={(e, v) => {
//               setInsuranceSearchTerm(v)
//               onInsuranceChange(v)
//             }}
//             loading={loadingCompanies}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<HealthAndSafety sx={{
//                   fontSize: { xs: 16, sm: 18, md: 20 },
//                   color: '#3BAE78'
//                 }} />}
//                 placeholder={isSmallMobile && compact ? "Insurance" : "Select Insurance"}
//                 compact={compact}
//                 InputProps={{
//                   ...params.InputProps,
//                   endAdornment: (
//                     <>
//                       {loadingCompanies ? <CircularProgress color="inherit" size={20} /> : null}
//                       {params.InputProps.endAdornment}
//                     </>
//                   ),
//                 }}
//               />
//             )}
//             filterOptions={(options, state) => options}
//             renderOption={(props, option) => (
//               <li {...props} key={typeof option === 'string' ? option : option._id}>
//                 {typeof option === 'string' ? option : option.payer_name}
//               </li>
//             )}
//           />
//         </FilterBox>

//         {/* Needs Input (Specialties) */}
//         <FilterBox compact={compact} isLast={!showSearchButton}>
//           <FilterLabel title="Needs" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={specialties}
//             getOptionLabel={(option) => {
//               if (typeof option === 'string') return option
//               return option.name
//             }}
//             value={needs}
//             onChange={(e, v) => onNeedsChange(typeof v === 'string' ? v : v?.name || null)}
//             onInputChange={(e, v) => onNeedsChange(v)}
//             loading={loadingSpecialties}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<AddCircleIcon sx={{
//                   fontSize: { xs: 16, sm: 18, md: 20 },
//                   color: '#3BAE78'
//                 }} />}
//                 placeholder={isSmallMobile && compact ? "Needs" : "What do you need?"}
//                 compact={compact}
//                 InputProps={{
//                   ...params.InputProps,
//                   endAdornment: (
//                     <>
//                       {loadingSpecialties ? <CircularProgress color="inherit" size={20} /> : null}
//                       {params.InputProps.endAdornment}
//                     </>
//                   ),
//                 }}
//               />
//             )}
//             renderOption={(props, option) => (
//               <li {...props} key={typeof option === 'string' ? option : option._id}>
//                 {typeof option === 'string' ? option : option.name}
//               </li>
//             )}
//           />
//         </FilterBox>

//         {/* Search Button */}
//         {showSearchButton && (
//           <Button
//             variant="contained"
//             onClick={handleSearch}
//             sx={{
//               m: compact ? 0.5 : { xs: 1, sm: 0.5 },
//               bgcolor: '#3BAE78',
//               borderRadius: compact ? 2 : { xs: 2, sm: 2.2 },
//               minWidth: { xs: '100%', sm: compact ? '50px' : '60px' },
//               width: { xs: 'calc(100% - 16px)', sm: 'auto' },
//               height: compact ? { xs: '40px', sm: 'auto' } : { xs: '48px', sm: 'auto' },
//               flex: { xs: '0 0 auto', sm: '0 0 auto' },
//               alignSelf: { xs: 'center', sm: 'stretch' },
//               '&:hover': {
//                 bgcolor: '#2d8c5f',
//                 transform: 'translateY(-1px)',
//                 boxShadow: '0 4px 12px rgba(59, 174, 120, 0.3)'
//               },
//               transition: 'all 0.3s ease',
//               fontSize: { xs: '0.875rem', sm: '1rem' }
//             }}
//           >
//             <Search sx={{
//               color: 'white',
//               fontSize: { xs: 20, sm: 24 }
//             }} />
//           </Button>
//         )}
//       </Box>
//     </Paper>
//   )
// }

// // Keep your existing FilterBox, FilterLabel, and StyledInput components exactly as they are
// const FilterBox = ({ children, compact, isFirst = false, isLast = false }: any) => (
//   <Box
//     sx={{
//       marginBottom: 0,
//       flex: { xs: '1 1 100%', sm: compact ? 1 : '1 1 25%' },
//       borderRight: {
//         xs: 'none',
//         sm: isLast && !compact ? 'none' : '1px solid #e0e0e0'
//       },
//       borderBottom: {
//         xs: compact ? 'none' : '1px solid #e0e0e0',
//         sm: 'none'
//       },
//       borderColor: 'grey.200',
//       bgcolor: 'grey.50',
//       display: 'flex',
//       flexDirection: 'column',
//       justifyContent: 'center',
//       p: compact ? { xs: 0.75, sm: 1 } : { xs: 1.5, sm: 2 },
//       minHeight: compact ? 'auto' : { xs: '60px', sm: '80px' },
//       '&:first-of-type': {
//         borderTopLeftRadius: { sm: compact ? 0 : 4 },
//         borderBottomLeftRadius: { sm: compact ? 0 : 4 },
//       },
//       '&:last-of-type': {
//         borderBottomRightRadius: { sm: compact ? 0 : 4 },
//       }
//     }}
//   >
//     {children}
//   </Box>
// )

// const FilterLabel = ({ title, compact }: { title: string; compact: boolean }) => (
//   <Typography
//     variant="caption"
//     sx={{
//       color: 'grey.600',
//       mb: compact ? { xs: 0, sm: 0.25 } : { xs: 0.5, sm: 1 },
//       fontSize: { xs: '0.7rem', sm: '0.75rem' },
//       fontWeight: compact ? 500 : 400,
//       display: { xs: compact ? 'none' : 'block', sm: 'block' },
//       lineHeight: 1.2
//     }}
//   >
//     {title}
//   </Typography>
// )

// const StyledInput = ({ icon, placeholder, compact, InputProps, ...params }: any) => (
//   <TextField
//     {...params}
//     placeholder={placeholder}
//     variant="standard"
//     InputProps={{
//       ...params.InputProps,
//       ...InputProps,
//       startAdornment: (
//         <InputAdornment
//           position="start"
//           sx={{
//             mr: { xs: 0.5, sm: 1 },
//             minWidth: 'auto'
//           }}
//         >
//           {icon}
//         </InputAdornment>
//       ),
//       disableUnderline: true,
//       sx: {
//         fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
//         height: compact ? { xs: '28px', sm: '32px' } : { xs: '36px', sm: '40px' },
//         '& .MuiInputBase-input': {
//           padding: compact ? '4px 0' : '8px 0',
//           fontSize: 'inherit',
//           '&::placeholder': {
//             fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
//             opacity: 0.7
//           }
//         }
//       }
//     }}
//     sx={{
//       width: '100%',
//       minWidth: '0 !important',
//       '& .MuiInputBase-root': {
//         padding: 0,
//         height: '100%',
//       },
//       ...params.sx,
//     }}
//   />
// )

// export default SearchBarForPatient

// import React from 'react'
// import {
//   Box,
//   Button,
//   Paper,
//   Typography,
//   Autocomplete,
//   TextField,
//   InputAdornment,
//   SxProps,
//   Theme,
// } from '@mui/material'
// import {
//   LocationOn,
//   Search,
//   HealthAndSafety,
//   Psychology,
//   Explore,
// } from '@mui/icons-material'
// import MyLocationIcon from '@mui/icons-material/MyLocation';
// // Dummy data for dropdowns
// export const LOCATIONS = ['Los Angeles, California', 'San Francisco, California', 'San Diego, California', 'New York, New York', 'Chicago, Illinois']
// export const INSURANCE_PROVIDERS = ['Aetna', 'Blue Cross Blue Shield', 'UnitedHealthcare', 'Cigna', 'Humana', 'Medicare', 'Medicaid']
// export const NEEDS = ['Therapy', 'Anxiety Counseling', 'Depression', 'PTSD', 'Family Therapy', 'Couples Therapy', 'Child Therapy']
// export const SEARCH_RADIUS = ['5 km', '10 km', '20 km', '40 km']

// interface SearchBarForPatientProps {
//   locations: string | null
//   radius: string | null
//   insurance: string | null
//   needs: string | null
//   onLocationChange: (value: string | null) => void
//   onRadiusChange: (value: string | null) => void
//   onInsuranceChange: (value: string | null) => void
//   onNeedsChange: (value: string | null) => void
//   onSearch?: () => void
//   showSearchButton?: boolean
//   compact?: boolean
//   sx?: SxProps<Theme>
// }

// const SearchBarForPatient: React.FC<SearchBarForPatientProps> = ({
//   locations,
//   radius,
//   insurance,
//   needs,
//   onLocationChange,
//   onRadiusChange,
//   onInsuranceChange,
//   onNeedsChange,
//   onSearch,
//   showSearchButton = true,
//   compact = false,
//   sx,
// }) => {
//   return (
//     <Paper
//       elevation={3}
//       sx={{
//         border: '1px solid lightgray',
//         borderRadius: compact ? 3 : 4,
//         overflow: 'hidden',
//         boxShadow: compact
//           ? '1px 2px 8px rgba(0,0,0,0.1)'
//           : '1px 8px 32px rgba(0,0,0,0.1)',
//         transition: 'all 0.3s ease',
//         height: compact ? '60px' : 'auto',
//         '&:hover': {
//           boxShadow: compact
//             ? '1px 4px 12px rgba(0,0,0,0.15)'
//             : '1px 12px 40px rgba(0,0,0,0.15)',
//         },
//         ...sx,
//       }}
//     >
//       <Box
//         sx={{
//           display: 'flex',
//           alignItems: 'stretch',
//           flexWrap: compact ? 'nowrap' : 'wrap',
//           height: compact ? '100%' : 'auto',
//         }}
//       >
//         {/* Location Input */}
//         <FilterBox fullWidth>
//           <FilterLabel title="Add Location" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={LOCATIONS}
//             value={locations}
//             onChange={(e, v) => onLocationChange(v)}
//             onInputChange={(e, v) => onLocationChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<MyLocationIcon sx={{ fontSize: 20, p: 0, color: '#3BAE78' }} />}
//                 placeholder="City, State or Zip code"
//                 compact={compact}
//                 sx={{ fontSize: 10, width: 120, p: 0 }}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Radius Input */}
//         <FilterBox fullWidth>
//           <FilterLabel title="Search Radius" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={SEARCH_RADIUS}
//             value={radius}
//             onChange={(e, v) => onRadiusChange(v)}
//             onInputChange={(e, v) => onRadiusChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<Explore  sx={{ fontSize: 20, p: 0, color: '#3BAE78' }}  />}
//                 placeholder="5 km, 10 km or custom"
//                 compact={compact}
//                 sx={{ fontSize: 10, width: 120, p: 0  }}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Insurance Input */}
//         <FilterBox fullWidth>
//           <FilterLabel title="Insurance" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={INSURANCE_PROVIDERS}
//             value={insurance}
//             onChange={(e, v) => onInsuranceChange(v)}
//             onInputChange={(e, v) => onInsuranceChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<HealthAndSafety sx={{ fontSize: 20, p: 0, color: '#3BAE78' }}  />}
//                 placeholder="Select Insurance"
//                 compact={compact}
//                 sx={{ fontSize: 10, width: 120, p: 0  }}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Needs Input */}
//         <FilterBox fullWidth>
//           <FilterLabel title="Needs" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={NEEDS}
//             value={needs}
//             onChange={(e, v) => onNeedsChange(v)}
//             onInputChange={(e, v) => onNeedsChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<Psychology sx={{ fontSize: 20, p: 0, color: '#3BAE78' }}  />}
//                 placeholder="What do you need?"
//                 compact={compact}
//                 sx={{ fontSize: 10, width: 120 }}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Search Button */}
//         {showSearchButton && onSearch && (
//           <Button
//             variant="contained"
//             onClick={onSearch}
//             sx={{
//               m: 0.4,
//               bgcolor: '#3BAE78',
//               borderRadius: 2.2,
//               minWidth: compact ? '40px' : { xs: '40px', md: '25px', lg: '10px' },
//               flex: { xs: '1 1 100%', md: '0 0 auto' },
//               '&:hover': { bgcolor: '#3BAE78' },
//             }}
//           >
//             <Search sx={{ color: 'white'}} />
//           </Button>
//         )}
//       </Box>
//     </Paper>
//   )
// }

// // 🔹 Reusable sub-components
// const FilterBox = ({ children }: any) => (
//   <Box
//     sx={{
//       flex: { xs: '1 1 100%', sm: '1 1 33%', md: 0 },
//       borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
//       borderColor: 'grey.200',
//       bgcolor: 'grey.50',
//       display: 'flex',
//       flexDirection: 'column',
//       justifyContent: 'center',
//       p: 1.5,
//     }}
//   >
//     {children}
//   </Box>
// )

// const FilterLabel = ({ title, compact }: { title: string; compact: boolean }) => (
//   <Typography
//     variant="caption"
//     sx={{ color: 'grey.500', mb: compact ? 0.25 : 0.5 }}
//   >
//     {title}
//   </Typography>
// )

// const StyledInput = ({ icon, placeholder, compact, ...params }: any) => (
//   <TextField
//     {...params}
//     placeholder={placeholder}
//     variant="standard"
//     InputProps={{
//       ...params.InputProps,
//       startAdornment: (
//         <>
//           <InputAdornment position="start">{icon}</InputAdornment>
//           {params.InputProps.startAdornment}
//         </>
//       ),
//       disableUnderline: true,
//     }}
//   />
// )

// export default SearchBarForPatient

// import React from 'react'
// import {
//   Box,
//   Button,
//   Paper,
//   Typography,
//   Autocomplete,
//   TextField,
//   InputAdornment,
//   SxProps,
//   Theme,
//   MenuItem,
//   Select,
//   FormControl,
//   Chip,
// } from '@mui/material'
// import {
//   LocationOn,
//   Search,
//   HealthAndSafety,
//   Psychology,
//   Add,
//   Close,
// } from '@mui/icons-material'

// // Dummy data for dropdowns
// export const LOCATIONS = [
//   'Los Angeles, California',
//   'San Francisco, California',
//   'San Diego, California',
//   'Sacramento, California',
//   'New York, New York',
//   'Brooklyn, New York',
//   'Chicago, Illinois',
//   'Houston, Texas',
//   'Dallas, Texas',
//   'Phoenix, Arizona',
//   'Philadelphia, Pennsylvania',
//   'Miami, Florida',
//   'Orlando, Florida',
//   'Seattle, Washington',
//   'Boston, Massachusetts',
// ]

// export const INSURANCE_PROVIDERS = [
//   'Aetna',
//   'Blue Cross Blue Shield',
//   'UnitedHealthcare',
//   'Cigna',
//   'Humana',
//   'Kaiser Permanente',
//   'Medicare',
//   'Medicaid',
//   'Anthem',
//   'Molina Healthcare',
//   'Centene',
//   'WellCare',
//   'Tricare',
//   'Oscar Health',
//   'Bright Health',
// ]

// export const NEEDS = [
//   'Anxiety',
//   'Depression',
//   'PTSD',
//   'Trauma',
//   'Grief & Loss',
//   'Relationship Issues',
//   'Family Therapy',
//   'Couples Therapy',
//   'Child Therapy',
//   'Adolescent Therapy',
//   'ADHD',
//   'Autism Spectrum',
//   'Eating Disorders',
//   'Substance Abuse',
//   'Anger Management',
//   'Stress Management',
//   'Life Transitions',
//   'Self-Esteem',
//   'Bipolar Disorder',
//   'OCD',
//   'Speech Therapy',
//   'Physical Therapy',
//   'Occupational Therapy',
// ]

// const RADIUS_OPTIONS = [
//   { value: '5', label: '5km' },
//   { value: '10', label: '10km' },
//   { value: '20', label: '20km' },
//   { value: '40', label: '40km' },
// ]

// interface SearchBarProps {
//   location: string[]
//   radius: string
//   insurance: string | null
//   need: string | null
//   onLocationsChange: (location: string[]) => void
//   onRadiusChange: (value: string) => void
//   onInsuranceChange: (value: string | null) => void
//   onNeedChange: (value: string | null) => void
//   onSearch?: () => void
//   showSearchButton?: boolean
//   compact?: boolean
//   sx?: SxProps<Theme>
// }

// const SearchBar: React.FC<SearchBarProps> = ({
//   location,
//   radius,
//   insurance,
//   need,
//   onLocationsChange,
//   onRadiusChange,
//   onInsuranceChange,
//   onNeedChange,
//   onSearch,
//   showSearchButton = true,
//   compact = false,
//   sx,
// }) => {
//   const handleAddLocation = (newLocation: string | null) => {
//     if (newLocation && !location.includes(newLocation)) {
//       onLocationsChange([...location, newLocation])
//     }
//   }

//   const handleRemoveLocation = (locationToRemove: string) => {
//     onLocationsChange(location.filter(loc => loc !== locationToRemove))
//   }

//   const handleRadiusInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const value = event.target.value
//     // Allow only numbers and empty string
//     if (value === '' || /^\d+$/.test(value)) {
//       onRadiusChange(value)
//     }
//   }

//   return (
//     <Paper
//       elevation={3}
//       sx={{
//         borderRadius: compact ? 2 : 3,
//         overflow: 'hidden',
//         boxShadow: compact
//           ? '0 2px 8px rgba(0,0,0,0.1)'
//           : '0 8px 32px rgba(0,0,0,0.1)',
//         transition: 'all 0.3s ease',
//         height: compact ? '60px' : 'auto',
//         '&:hover': {
//           boxShadow: compact
//             ? '0 4px 12px rgba(0,0,0,0.15)'
//             : '0 12px 40px rgba(0,0,0,0.15)',
//           transform: compact ? 'none' : 'translateY(-2px)',
//         },
//         ...sx,
//       }}
//     >
//       <Box
//         sx={{
//           display: 'flex',
//           alignItems: 'stretch',
//           flexWrap: compact ? 'nowrap' : 'wrap',
//           height: compact ? '100%' : 'auto',
//         }}
//       >
//         {/* Locations Input - Now supports multiple location */}
//         <Box
//           sx={{
//             flex: { xs: '1 1 100%', sm: '1 1 33%', md: 1 },
//             borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
//             borderBottom: { xs: '1px solid', md: 'none' },
//             borderColor: 'grey.200',
//             bgcolor: 'grey.50',
//             transition: 'all 0.3s ease',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             '&:hover': {
//               bgcolor: 'grey.100',
//             },
//           }}
//         >
//           <Box
//             sx={{
//               p: compact ? 1 : { sm: 1.5, md: 2 },
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               height: compact ? '100%' : 'auto',
//             }}
//           >
//             <Typography
//               variant="caption"
//               sx={{
//                 color: 'grey.500',
//                 mb: compact ? 0.25 : 0.5,
//                 display: 'block',
//                 fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
//                 lineHeight: compact ? 1 : 'normal',
//               }}
//             >
//               Add Locations
//             </Typography>

//             {/* Location chips for selected location */}
//             {location.length > 0 && (
//               <Box sx={{ mb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
//                 {location.map((location, index) => (
//                   <Chip
//                     key={index}
//                     label={location}
//                     size="small"
//                     onDelete={() => handleRemoveLocation(location)}
//                     deleteIcon={<Close />}
//                     sx={{
//                       height: '20px',
//                       fontSize: '0.6rem',
//                       '& .MuiChip-deleteIcon': {
//                         fontSize: '0.8rem',
//                       },
//                     }}
//                   />
//                 ))}
//               </Box>
//             )}

//             <Autocomplete
//               freeSolo
//               options={LOCATIONS}
//               value={null}
//               onChange={(event, newValue) => handleAddLocation(newValue)}
//               onInputChange={(event, newInputValue) => {
//                 // Only add on Enter key or blur if you want, but we'll keep it for dropdown selection
//               }}
//               renderInput={params => (
//                 <TextField
//                   {...params}
//                   placeholder="Add location..."
//                   variant="standard"
//                   InputProps={{
//                     ...params.InputProps,
//                     startAdornment: (
//                       <>
//                         <InputAdornment position="start">
//                           <Add
//                             sx={{
//                               color: '#2E7DAF',
//                               fontSize: compact
//                                 ? '1rem'
//                                 : { sm: '1.2rem', md: '1.5rem' },
//                               transition: 'transform 0.2s ease',
//                               '&:hover': {
//                                 transform: 'scale(1.1)',
//                               },
//                             }}
//                           />
//                         </InputAdornment>
//                         {params.InputProps.startAdornment}
//                       </>
//                     ),
//                     disableUnderline: true,
//                     sx: {
//                       fontSize: compact
//                         ? '0.75rem'
//                         : { sm: '0.8rem', md: '0.875rem' },
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                   sx={{
//                     '& .MuiInputBase-input': {
//                       py: compact ? 0.5 : 0,
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                 />
//               )}
//             />
//           </Box>
//         </Box>

//         {/* Search Radius Input */}
//         <Box
//           sx={{
//             flex: { xs: '1 1 100%', sm: '1 1 33%', md: 1 },
//             borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
//             borderBottom: { xs: '1px solid', md: 'none' },
//             borderColor: 'grey.200',
//             bgcolor: 'grey.50',
//             transition: 'all 0.3s ease',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             '&:hover': {
//               bgcolor: 'grey.100',
//             },
//           }}
//         >
//           <Box
//             sx={{
//               p: compact ? 1 : { sm: 1.5, md: 2 },
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               height: compact ? '100%' : 'auto',
//             }}
//           >
//             <Typography
//               variant="caption"
//               sx={{
//                 color: 'grey.500',
//                 mb: compact ? 0.25 : 0.5,
//                 display: 'block',
//                 fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
//                 lineHeight: compact ? 1 : 'normal',
//               }}
//             >
//               Search Radius
//             </Typography>
//             <FormControl variant="standard" fullWidth>
//               <Select
//                 value={radius}
//                 onChange={(event) => onRadiusChange(event.target.value)}
//                 displayEmpty
//                 disableUnderline
//                 sx={{
//                   fontSize: compact ? '0.75rem' : { sm: '0.8rem', md: '0.875rem' },
//                   height: compact ? '24px' : 'auto',
//                   '& .MuiSelect-select': {
//                     py: compact ? 0.5 : 0.75,
//                     display: 'flex',
//                     alignItems: 'center',
//                   },
//                 }}
//                 renderValue={(value) => {
//                   const option = RADIUS_OPTIONS.find(opt => opt.value === value)
//                   return option ? option.label : value ? `${value}km` : 'Select radius'
//                 }}
//               >
//                 {RADIUS_OPTIONS.map((option) => (
//                   <MenuItem
//                     key={option.value}
//                     value={option.value}
//                     sx={{ fontSize: compact ? '0.75rem' : '0.875rem' }}
//                   >
//                     {option.label}
//                   </MenuItem>
//                 ))}
//                 <MenuItem value="">
//                   <em>Custom distance</em>
//                 </MenuItem>
//               </Select>
//             </FormControl>

//             {/* Custom radius input */}
//             {!radius && (
//               <TextField
//                 placeholder="Enter km"
//                 variant="standard"
//                 value={radius}
//                 onChange={handleRadiusInputChange}
//                 InputProps={{
//                   disableUnderline: true,
//                   sx: {
//                     fontSize: compact ? '0.75rem' : { sm: '0.8rem', md: '0.875rem' },
//                     height: compact ? '24px' : 'auto',
//                     mt: 0.5,
//                   },
//                 }}
//                 sx={{
//                   '& .MuiInputBase-input': {
//                     py: compact ? 0.5 : 0,
//                     height: compact ? '24px' : 'auto',
//                   },
//                 }}
//               />
//             )}
//           </Box>
//         </Box>

//         {/* Insurance Input */}
//         <Box
//           sx={{
//             flex: { xs: '1 1 100%', sm: '1 1 33%', md: 1 },
//             borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
//             borderBottom: { xs: '1px solid', md: 'none' },
//             borderColor: 'grey.200',
//             bgcolor: 'grey.50',
//             transition: 'all 0.3s ease',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             '&:hover': {
//               bgcolor: 'grey.100',
//             },
//           }}
//         >
//           <Box
//             sx={{
//               p: compact ? 1 : { sm: 1.5, md: 2 },
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               height: compact ? '100%' : 'auto',
//             }}
//           >
//             <Typography
//               variant="caption"
//               sx={{
//                 color: 'grey.500',
//                 mb: compact ? 0.25 : 0.5,
//                 display: 'block',
//                 fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
//                 lineHeight: compact ? 1 : 'normal',
//               }}
//             >
//               Insurance
//             </Typography>
//             <Autocomplete
//               freeSolo
//               options={INSURANCE_PROVIDERS}
//               value={insurance}
//               onChange={(event, newValue) => onInsuranceChange(newValue)}
//               onInputChange={(event, newInputValue) =>
//                 onInsuranceChange(newInputValue)
//               }
//               renderInput={params => (
//                 <TextField
//                   {...params}
//                   placeholder="Select or enter insurance"
//                   variant="standard"
//                   InputProps={{
//                     ...params.InputProps,
//                     startAdornment: (
//                       <>
//                         <InputAdornment position="start">
//                           <HealthAndSafety
//                             sx={{
//                               color: '#2E7DAF',
//                               fontSize: compact
//                                 ? '1rem'
//                                 : { sm: '1.2rem', md: '1.5rem' },
//                               transition: 'transform 0.2s ease',
//                               '&:hover': {
//                                 transform: 'scale(1.1)',
//                               },
//                             }}
//                           />
//                         </InputAdornment>
//                         {params.InputProps.startAdornment}
//                       </>
//                     ),
//                     disableUnderline: true,
//                     sx: {
//                       fontSize: compact
//                         ? '0.75rem'
//                         : { sm: '0.8rem', md: '0.875rem' },
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                   sx={{
//                     '& .MuiInputBase-input': {
//                       py: compact ? 0.5 : 0,
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                 />
//               )}
//             />
//           </Box>
//         </Box>

//         {/* Needs Input (replaces Specialization) */}
//         <Box
//           sx={{
//             flex: { xs: '1 1 100%', sm: '1 1 33%', md: 1 },
//             borderRight: showSearchButton
//               ? { xs: 'none', md: '1px solid #e0e0e0' }
//               : 'none',
//             borderColor: 'grey.200',
//             bgcolor: 'grey.50',
//             transition: 'all 0.3s ease',
//             display: 'flex',
//             flexDirection: 'column',
//             justifyContent: 'center',
//             '&:hover': {
//               bgcolor: 'grey.100',
//             },
//           }}
//         >
//           <Box
//             sx={{
//               p: compact ? 1 : { sm: 1.5, md: 2 },
//               display: 'flex',
//               flexDirection: 'column',
//               justifyContent: 'center',
//               height: compact ? '100%' : 'auto',
//             }}
//           >
//             <Typography
//               variant="caption"
//               sx={{
//                 color: 'grey.500',
//                 mb: compact ? 0.25 : 0.5,
//                 display: 'block',
//                 fontSize: compact ? '0.6rem' : { sm: '0.7rem', md: '0.75rem' },
//                 lineHeight: compact ? 1 : 'normal',
//               }}
//             >
//               Needs
//             </Typography>
//             <Autocomplete
//               freeSolo
//               options={NEEDS}
//               value={need}
//               onChange={(event, newValue) => onNeedChange(newValue)}
//               onInputChange={(event, newInputValue) =>
//                 onNeedChange(newInputValue)
//               }
//               renderInput={params => (
//                 <TextField
//                   {...params}
//                   placeholder="Select or enter needs"
//                   variant="standard"
//                   InputProps={{
//                     ...params.InputProps,
//                     startAdornment: (
//                       <>
//                         <InputAdornment position="start">
//                           <Psychology
//                             sx={{
//                               color: '#2E7DAF',
//                               fontSize: compact
//                                 ? '1rem'
//                                 : { sm: '1.2rem', md: '1.5rem' },
//                               transition: 'transform 0.2s ease',
//                               '&:hover': {
//                                 transform: 'scale(1.1)',
//                               },
//                             }}
//                           />
//                         </InputAdornment>
//                         {params.InputProps.startAdornment}
//                       </>
//                     ),
//                     disableUnderline: true,
//                     sx: {
//                       fontSize: compact
//                         ? '0.75rem'
//                         : { sm: '0.8rem', md: '0.875rem' },
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                   sx={{
//                     '& .MuiInputBase-input': {
//                       py: compact ? 0.5 : 0,
//                       height: compact ? '24px' : 'auto',
//                     },
//                   }}
//                 />
//               )}
//             />
//           </Box>
//         </Box>

//         {/* Search Button */}
//         {showSearchButton && onSearch && (
//           <Button
//             variant="contained"
//             onClick={onSearch}
//             sx={{
//               bgcolor: '#2E7DAF',
//               borderRadius: 0,
//               minWidth: compact
//                 ? '50px'
//                 : { xs: '100%', md: '60px', lg: '80px' },
//               flex: { xs: '1 1 100%', md: '0 0 auto' },
//               height: compact ? '100%' : 'auto',
//               transition: 'all 0.3s ease',
//               '&:hover': {
//                 bgcolor: '#2563a0',
//                 transform: compact ? 'none' : 'scale(1.05)',
//                 boxShadow: '0 4px 12px rgba(46, 125, 175, 0.3)',
//               },
//             }}
//           >
//             <Search
//               sx={{
//                 color: 'white',
//                 fontSize: compact ? '1.2rem' : { sm: '1.5rem', md: '2rem' },
//               }}
//             />
//           </Button>
//         )}
//       </Box>
//     </Paper>
//   )
// }

// export default SearchBar

// import React from 'react'
// import {
//   Box,
//   Button,
//   Paper,
//   Typography,
//   Autocomplete,
//   TextField,
//   InputAdornment,
//   SxProps,
//   Theme,
// } from '@mui/material'
// import {
//   LocationOn,
//   Search,
//   HealthAndSafety,
//   Psychology,
//   Explore,
// } from '@mui/icons-material'

// // Dummy data for dropdowns
// export const LOCATIONS = ['Los Angeles, California', 'San Francisco, California', 'San Diego, California', 'New York, New York', 'Chicago, Illinois']
// export const INSURANCE_PROVIDERS = ['Aetna', 'Blue Cross Blue Shield', 'UnitedHealthcare', 'Cigna', 'Humana', 'Medicare', 'Medicaid']
// export const NEEDS = ['Therapy', 'Anxiety Counseling', 'Depression', 'PTSD', 'Family Therapy', 'Couples Therapy', 'Child Therapy']
// export const SEARCH_RADIUS = ['5 km', '10 km', '20 km', '40 km']

// interface SearchBarForPatientProps {
//   location: string | null
//   radius: string | null
//   insurance: string | null
//   needs: string | null
//   onLocationChange: (value: string | null) => void
//   onRadiusChange: (value: string | null) => void
//   onInsuranceChange: (value: string | null) => void
//   onNeedsChange: (value: string | null) => void
//   onSearch?: () => void
//   showSearchButton?: boolean
//   compact?: boolean
//   sx?: SxProps<Theme>
// }

// const SearchBarForPatient: React.FC<SearchBarForPatientProps> = ({
//   location,
//   radius,
//   insurance,
//   needs,
//   onLocationChange,
//   onRadiusChange,
//   onInsuranceChange,
//   onNeedsChange,
//   onSearch,
//   showSearchButton = true,
//   compact = false,
//   sx,
// }) => {
//   return (
//     <Paper
//       elevation={3}
//       sx={{
//         borderRadius: compact ? 2 : 3,
//         overflow: 'hidden',
//         boxShadow: compact
//           ? '0 2px 8px rgba(0,0,0,0.1)'
//           : '0 8px 32px rgba(0,0,0,0.1)',
//         transition: 'all 0.3s ease',
//         height: compact ? '60px' : 'auto',
//         '&:hover': {
//           boxShadow: compact
//             ? '0 4px 12px rgba(0,0,0,0.15)'
//             : '0 12px 40px rgba(0,0,0,0.15)',
//         },
//         ...sx,
//       }}
//     >
//       <Box
//         sx={{
//           display: 'flex',
//           alignItems: 'stretch',
//           flexWrap: compact ? 'nowrap' : 'wrap',
//           height: compact ? '100%' : 'auto',
//         }}
//       >
//         {/* Location Input */}
//         <FilterBox>
//           <FilterLabel title="Add Location" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={LOCATIONS}
//             value={location}
//             onChange={(e, v) => onLocationChange(v)}
//             onInputChange={(e, v) => onLocationChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<LocationOn />}
//                 placeholder="City, State or Zip code"
//                 compact={compact}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Radius Input */}
//         <FilterBox>
//           <FilterLabel title="Search Radius" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={SEARCH_RADIUS}
//             value={radius}
//             onChange={(e, v) => onRadiusChange(v)}
//             onInputChange={(e, v) => onRadiusChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<Explore />}
//                 placeholder="5 km, 10 km or custom"
//                 compact={compact}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Insurance Input */}
//         <FilterBox>
//           <FilterLabel title="Insurance" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={INSURANCE_PROVIDERS}
//             value={insurance}
//             onChange={(e, v) => onInsuranceChange(v)}
//             onInputChange={(e, v) => onInsuranceChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<HealthAndSafety />}
//                 placeholder="Select Insurance"
//                 compact={compact}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Needs Input */}
//         <FilterBox>
//           <FilterLabel title="Needs" compact={compact} />
//           <Autocomplete
//             freeSolo
//             options={NEEDS}
//             value={needs}
//             onChange={(e, v) => onNeedsChange(v)}
//             onInputChange={(e, v) => onNeedsChange(v)}
//             renderInput={params => (
//               <StyledInput
//                 {...params}
//                 icon={<Psychology />}
//                 placeholder="What do you need?"
//                 compact={compact}
//               />
//             )}
//           />
//         </FilterBox>

//         {/* Search Button */}
//         {showSearchButton && onSearch && (
//           <Button
//             variant="contained"
//             onClick={onSearch}
//             sx={{
//               bgcolor: '#2E7DAF',
//               borderRadius: 0,
//               minWidth: compact ? '50px' : { xs: '100%', md: '60px', lg: '80px' },
//               flex: { xs: '1 1 100%', md: '0 0 auto' },
//               '&:hover': { bgcolor: '#2563a0' },
//             }}
//           >
//             <Search sx={{ color: 'white' }} />
//           </Button>
//         )}
//       </Box>
//     </Paper>
//   )
// }

// // 🔹 Reusable sub-components
// const FilterBox = ({ children }: any) => (
//   <Box
//     sx={{
//       flex: { xs: '1 1 100%', sm: '1 1 33%', md: 1 },
//       borderRight: { xs: 'none', md: '1px solid #e0e0e0' },
//       borderColor: 'grey.200',
//       bgcolor: 'grey.50',
//       display: 'flex',
//       flexDirection: 'column',
//       justifyContent: 'center',
//       p: 1.5,
//     }}
//   >
//     {children}
//   </Box>
// )

// const FilterLabel = ({ title, compact }: { title: string; compact: boolean }) => (
//   <Typography
//     variant="caption"
//     sx={{ color: 'grey.500', mb: compact ? 0.25 : 0.5 }}
//   >
//     {title}
//   </Typography>
// )

// const StyledInput = ({ icon, placeholder, compact, ...params }: any) => (
//   <TextField
//     {...params}
//     placeholder={placeholder}
//     variant="standard"
//     InputProps={{
//       ...params.InputProps,
//       startAdornment: (
//         <>
//           <InputAdornment position="start">{icon}</InputAdornment>
//           {params.InputProps.startAdornment}
//         </>
//       ),
//       disableUnderline: true,
//     }}
//   />
// )

// export default SearchBarForPatient
